import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import axios from 'axios';

import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import getAuthSession from '../../services/auth';
import { getCompanyReferences } from '../../services/companyDetail';
import { USER_TYPES } from '../../services/user';
import {
  assignFeatureFlagUsers,
  createFeatureFlag,
  deleteFeatureFlag,
  getFeatureFlags,
  getFeatureFlagUsers,
  patchFeatureFlag,
  removeFeatureFlagUsers,
} from '../../services/featureFlags';
import { FeatureFlag, FeatureFlagUser } from '../../interfaces/featureFlag.interface';

const USER_TYPE_OPTIONS = Object.values(USER_TYPES);

const USER_TYPE_LABELS: Record<string, string> = {
  tpn_admin: 'TPN Admins',
  vendor_admin: 'Service Providers',
  content_owner: 'Content Owners',
  assessor: 'Assessors',
};

const userTypeLabel = (value: string): string => USER_TYPE_LABELS[value] ?? value;

interface CompanyOption {
  id: number;
  name: string;
}

interface FlagFormValues {
  description: string;
  isActive: boolean;
  isPublic: boolean;
  startsAt: string;
  endsAt: string;
  targetUserTypes: string[];
  targetCompanies: CompanyOption[];
}

const EMPTY_FORM = {
  name: '',
  codename: '',
  description: '',
  isActive: true,
  isPublic: false,
  startsAt: '',
  endsAt: '',
  targetUserTypes: [] as string[],
  targetCompanies: [] as CompanyOption[],
};

async function authHeader(): Promise<{ Authorization: string }> {
  const session = await getAuthSession();
  return { Authorization: `Bearer ${session.getIdToken().getJwtToken()}` };
}

async function searchCompanies(search: string): Promise<CompanyOption[]> {
  if (search.length < 2) return [];
  const res = await axios.get(`${process.env.REACT_APP_BASE_API}/companies/list/`, {
    headers: await authHeader(),
    params: { search },
  });
  return res.data.results as CompanyOption[];
}

async function searchUsersByEmail(email: string): Promise<FeatureFlagUser[]> {
  if (email.length < 2) return [];
  const res = await axios.get(`${process.env.REACT_APP_BASE_API}/users/`, {
    headers: await authHeader(),
    params: { email },
  });
  return (res.data.results as FeatureFlagUser[]).map((u) => ({
    id: u.id,
    firstName: u.firstName,
    lastName: u.lastName,
    email: u.email,
  }));
}

function toIsoOrNull(value: string): string | null {
  return value ? new Date(value).toISOString() : null;
}

function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface FlagFieldsProps {
  values: FlagFormValues;
  onChange: (patch: Partial<FlagFormValues>) => void;
  companyOptions: CompanyOption[];
  onCompanySearch: (query: string) => void;
}

/** The editable fields shared by the create and edit dialogs. */
function FlagFields({ values, onChange, companyOptions, onCompanySearch }: FlagFieldsProps) {
  return (
    <>
      <TextField
        label="Description"
        multiline
        minRows={2}
        value={values.description}
        onChange={(e) => onChange({ description: e.target.value })}
      />
      <Stack direction="row" gap={4}>
        <Stack>
          <FormControlLabel
            control={<Switch checked={values.isActive} onChange={(e) => onChange({ isActive: e.target.checked })} />}
            label="Active"
          />
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>
            Master switch. When off, the feature is off for <strong>everyone</strong> — even individually assigned users.
          </Typography>
        </Stack>
        <Stack>
          <FormControlLabel
            control={<Switch checked={values.isPublic} onChange={(e) => onChange({ isPublic: e.target.checked })} />}
            label="Public (everyone)"
          />
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>
            When on, every user gets the feature — no targeting or assignment needed.
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" gap={2}>
        <TextField
          label="Starts at"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          helperText="Optional. Off before this time."
          value={values.startsAt}
          onChange={(e) => onChange({ startsAt: e.target.value })}
        />
        <TextField
          label="Ends at"
          type="datetime-local"
          InputLabelProps={{ shrink: true }}
          helperText="Optional. Off after this time."
          value={values.endsAt}
          onChange={(e) => onChange({ endsAt: e.target.value })}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Leave a bound blank for open-ended. Outside the schedule the feature is off for everyone, even assigned users.
      </Typography>
      <Autocomplete
        multiple
        options={USER_TYPE_OPTIONS}
        value={values.targetUserTypes}
        getOptionLabel={(option) => userTypeLabel(option)}
        onChange={(_e, value) => onChange({ targetUserTypes: value })}
        renderInput={(params) => <TextField {...params} label="Target user types" />}
      />
      <Autocomplete
        multiple
        options={companyOptions}
        value={values.targetCompanies}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterOptions={(x) => x}
        onInputChange={(_e, value) => onCompanySearch(value)}
        onChange={(_e, value) => onChange({ targetCompanies: value })}
        renderInput={(params) => <TextField {...params} label="Target companies" placeholder="Search companies" />}
      />
    </>
  );
}

export default function FeatureFlagsAdminPanel() {
  const [rows, setRows] = useState<FeatureFlag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formError, setFormError] = useState<string | null>(null);

  const [editFlag, setEditFlag] = useState<FeatureFlag | null>(null);
  const [editForm, setEditForm] = useState<FlagFormValues | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<FeatureFlag | null>(null);

  const [usersFlag, setUsersFlag] = useState<FeatureFlag | null>(null);
  const [assignedUsers, setAssignedUsers] = useState<FeatureFlagUser[]>([]);
  const [userOptions, setUserOptions] = useState<FeatureFlagUser[]>([]);

  useEffect(() => {
    getFeatureFlags({ limit: 200 })
      .then((response) => setRows(response.results))
      .finally(() => setIsLoading(false));
  }, []);

  const handleCompanySearch = (query: string) => { searchCompanies(query).then(setCompanyOptions); };

  const updateRow = (flag: FeatureFlag) =>
    setRows((current) => current.map((row) => (row.id === flag.id ? flag : row)));

  const handleToggle = (flag: FeatureFlag, field: 'isActive' | 'isPublic') => {
    patchFeatureFlag(flag.id, { [field]: !flag[field] }).then(updateRow);
  };

  const handleCreate = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const created = await createFeatureFlag({
        name: form.name,
        codename: form.codename,
        description: form.description,
        isActive: form.isActive,
        isPublic: form.isPublic,
        startsAt: toIsoOrNull(form.startsAt),
        endsAt: toIsoOrNull(form.endsAt),
        targetUserTypes: form.targetUserTypes,
        targetCompanies: form.targetCompanies.map((c) => c.id),
      });
      setRows((current) => [created, ...current]);
      setForm({ ...EMPTY_FORM });
      setCreateOpen(false);
    } catch (error: any) {
      setFormError(error?.response?.data?.codename?.[0] ?? 'Could not create feature flag.');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (flag: FeatureFlag) => {
    setEditFlag(flag);
    setCompanyOptions([]);
    setEditForm({
      description: flag.description ?? '',
      isActive: flag.isActive,
      isPublic: flag.isPublic,
      startsAt: isoToLocalInput(flag.startsAt),
      endsAt: isoToLocalInput(flag.endsAt),
      targetUserTypes: flag.targetUserTypes ?? [],
      targetCompanies: [],
    });
    const ids = flag.targetCompanies ?? [];
    if (ids.length) {
      getCompanyReferences(ids).then((refs) => {
        const options = refs.map((r) => ({ id: r.id as number, name: r.name ?? '' }));
        setEditForm((current) => (current ? { ...current, targetCompanies: options } : current));
      });
    }
  };

  const handleEditSave = async () => {
    if (!editFlag || !editForm) return;
    setSaving(true);
    try {
      const updated = await patchFeatureFlag(editFlag.id, {
        description: editForm.description,
        isActive: editForm.isActive,
        isPublic: editForm.isPublic,
        startsAt: toIsoOrNull(editForm.startsAt),
        endsAt: toIsoOrNull(editForm.endsAt),
        targetUserTypes: editForm.targetUserTypes,
        targetCompanies: editForm.targetCompanies.map((c) => c.id),
      });
      updateRow(updated);
      setEditFlag(null);
      setEditForm(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    deleteFeatureFlag(id).then(() => {
      setRows((current) => current.filter((row) => row.id !== id));
      setDeleteTarget(null);
    });
  };

  const openUsers = (flag: FeatureFlag) => {
    setUsersFlag(flag);
    setUserOptions([]);
    getFeatureFlagUsers(flag.id, { limit: 200 }).then((response) => setAssignedUsers(response.results));
  };

  const handleAssign = (user: FeatureFlagUser | null) => {
    if (!usersFlag || !user) return;
    assignFeatureFlagUsers(usersFlag.id, [user.id]).then((flag) => {
      updateRow(flag);
      setUsersFlag(flag);
      setAssignedUsers((current) => (current.some((u) => u.id === user.id) ? current : [...current, user]));
    });
  };

  const handleRemove = (user: FeatureFlagUser) => {
    if (!usersFlag) return;
    removeFeatureFlagUsers(usersFlag.id, [user.id]).then((flag) => {
      updateRow(flag);
      setUsersFlag(flag);
      setAssignedUsers((current) => current.filter((u) => u.id !== user.id));
    });
  };

  const stop = (event: React.MouseEvent) => event.stopPropagation();

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'codename', headerName: 'Codename', width: 230 },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 90,
      renderCell: (params) => (
        <Switch
          checked={params.row.isActive}
          onClick={stop}
          onChange={() => handleToggle(params.row, 'isActive')}
          inputProps={{ 'aria-label': `toggle active ${params.row.codename}` }}
        />
      ),
    },
    {
      field: 'isPublic',
      headerName: 'Public',
      width: 90,
      renderCell: (params) => (
        <Switch
          checked={params.row.isPublic}
          onClick={stop}
          onChange={() => handleToggle(params.row, 'isPublic')}
          inputProps={{ 'aria-label': `toggle public ${params.row.codename}` }}
        />
      ),
    },
    {
      field: 'targets',
      headerName: 'Targeting',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const types = (params.row.targetUserTypes ?? []) as string[];
        const companyCount = (params.row.targetCompanies ?? []).length;
        const bits: string[] = [];
        if (types.length) bits.push(`types: ${types.map(userTypeLabel).join(', ')}`);
        if (companyCount) bits.push(`${companyCount} compan${companyCount === 1 ? 'y' : 'ies'}`);
        return <Typography variant="body2">{bits.join(' · ') || '—'}</Typography>;
      },
    },
    {
      field: 'assignedUserCount',
      headerName: 'Users',
      width: 120,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" gap={0.5} onClick={stop}>
          <span>{params.row.assignedUserCount}</span>
          <IconButton size="small" aria-label={`manage users ${params.row.codename}`} onClick={() => openUsers(params.row)}>
            <PeopleIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
    {
      field: 'action',
      headerName: 'Delete',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label={`delete ${params.row.codename}`}
          onClick={(e) => { stop(e); setDeleteTarget(params.row); }}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Card sx={{ padding: '2em' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>How a feature flag turns on for a user</AlertTitle>
        A user gets the feature only when it is <strong>Active</strong> <em>and</em> the current time is within its
        schedule window, <em>and</em> at least one of these is true:
        <ul style={{ margin: '0.5em 0 0', paddingLeft: '1.25em' }}>
          <li><strong>Public</strong> is on (everyone gets it), or</li>
          <li>the user&apos;s <strong>user type</strong> is targeted, or</li>
          <li>the user&apos;s <strong>company</strong> is targeted, or</li>
          <li>the user is <strong>individually assigned</strong> (the Users button).</li>
        </ul>
        Turning <strong>Active</strong> off — or being outside the schedule — disables the feature for everyone,
        including individually-assigned users. Click a row to edit all of these.
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Note:</strong> Public and targeting (type/company) only affect <em>client-side</em> features (e.g. the
          new company profile). Server-enforced permissions such as masquerade are honored only for users added
          individually via the <strong>Users</strong> button.
        </Typography>
      </Alert>
      <Stack direction="row-reverse" spacing={1} mb={2}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Add Feature Flag
        </Button>
      </Stack>

      <DataGrid
        style={{ height: 460, width: '100%' }}
        rows={rows}
        columns={columns}
        pageSize={25}
        rowsPerPageOptions={[25]}
        loading={isLoading}
        disableSelectionOnClick
        onRowClick={(params) => openEdit(params.row as FeatureFlag)}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />

      {/* Create */}
      <StandardDialog title="Add Feature Flag" handleClose={() => setCreateOpen(false)} isOpen={createOpen}>
        <Stack gap={2} sx={{ mt: 1 }}>
          <TextField
            required
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            required
            label="Codename"
            helperText="The permission codename the client checks (e.g. can_view_v2_company_profile)."
            value={form.codename}
            onChange={(e) => setForm({ ...form, codename: e.target.value })}
          />
          <FlagFields
            values={form}
            onChange={(patch) => setForm({ ...form, ...patch })}
            companyOptions={companyOptions}
            onCompanySearch={handleCompanySearch}
          />
          {formError && <Typography color="error">{formError}</Typography>}
        </Stack>
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving || !form.name || !form.codename}
            onClick={handleCreate}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            Add
          </Button>
        </StandardDialogActions>
      </StandardDialog>

      {/* Edit */}
      <StandardDialog
        title={editFlag ? `Edit — ${editFlag.name}` : 'Edit'}
        handleClose={() => { setEditFlag(null); setEditForm(null); }}
        isOpen={!!editFlag && !!editForm}
      >
        {editFlag && editForm && (
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={editFlag.name} disabled />
            <TextField
              label="Codename"
              value={editFlag.codename}
              disabled
              helperText="Codename is fixed once created (the client checks against it)."
            />
            <FlagFields
              values={editForm}
              onChange={(patch) => setEditForm({ ...editForm, ...patch })}
              companyOptions={companyOptions}
              onCompanySearch={handleCompanySearch}
            />
          </Stack>
        )}
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => { setEditFlag(null); setEditForm(null); }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleEditSave}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            Save
          </Button>
        </StandardDialogActions>
      </StandardDialog>

      {/* Manage users */}
      <StandardDialog
        title={usersFlag ? `Users — ${usersFlag.name}` : 'Users'}
        handleClose={() => setUsersFlag(null)}
        isOpen={!!usersFlag}
      >
        <Stack gap={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={userOptions}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
            onInputChange={(_e, value) => { searchUsersByEmail(value).then(setUserOptions); }}
            onChange={(_e, value) => handleAssign(value)}
            renderInput={(params) => <TextField {...params} label="Assign a user by email" />}
          />
          <Stack direction="row" gap={1} flexWrap="wrap">
            {assignedUsers.length === 0 && <Typography variant="body2">No users assigned.</Typography>}
            {assignedUsers.map((user) => (
              <Chip key={user.id} label={user.email} onDelete={() => handleRemove(user)} />
            ))}
          </Stack>
        </Stack>
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => setUsersFlag(null)}>Done</Button>
        </StandardDialogActions>
      </StandardDialog>

      {/* Delete */}
      <StandardDialog title="Delete Feature Flag" handleClose={() => setDeleteTarget(null)} isOpen={!!deleteTarget}>
        <Typography>Delete {deleteTarget?.name}?</Typography>
        <Typography color="error">
          This deletes the backing permission and revokes the feature from every user.
        </Typography>
        <StandardDialogActions>
          <Button variant="outlined" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </StandardDialogActions>
      </StandardDialog>
    </Card>
  );
}
